import type { LucideIcon } from "lucide-react";
import { FileCheck, Landmark, User } from "lucide-react";

/** Verification tier rank: 3 = bank (highest trust), 2 = doc, 1 = self. */
export type TierRank = 1 | 2 | 3;
export type TierId = "bank" | "doc" | "self";

export interface TierMeta {
  id: TierId;
  /** Full label, e.g. "Verified". */
  label: string;
  /** Short label for dense UI, e.g. "Verified". */
  short: string;
  rank: TierRank;
  icon: LucideIcon;
  /** Detail-header descriptor (design doc §4.3). */
  descriptor: string;
  /** Tailwind/CSS class root for badge variants. */
  className: string;
}

// Source of truth — design doc §6.5 + §4.3. Keyed by rank so a numeric tier
// from the API maps directly.
export const TIERS: Record<TierRank, TierMeta> = {
  3: {
    id: "bank",
    label: "Verified",
    short: "Verified",
    rank: 3,
    icon: Landmark,
    descriptor: "Securely sourced",
    className: "tier-bank",
  },
  2: {
    id: "doc",
    label: "Document",
    short: "Document",
    rank: 2,
    icon: FileCheck,
    descriptor: "Documents on file",
    className: "tier-doc",
  },
  1: {
    id: "self",
    label: "Self-reported",
    short: "Self",
    rank: 1,
    icon: User,
    descriptor: "Not yet verified",
    className: "tier-self",
  },
};

const ID_TO_RANK: Record<TierId, TierRank> = { bank: 3, doc: 2, self: 1 };

/**
 * Map the WIRE verification enum (model.FinancialVerificationTier:
 * self_reported | connected | document | pre_approval) to its display label.
 *
 * Surfaces that render `verification_tier` straight off the wire show the raw
 * enum to the pro ("Verification: connected"), which is what the copilot's
 * PersonCard did. Fail-closed on an unrecognised value: render nothing rather
 * than leak an identifier.
 */
export function verificationTierLabel(status: string | undefined): string | undefined {
  switch (status) {
    case "connected":
      return TIERS[3].label;
    case "document":
    case "pre_approval":
      return TIERS[2].label;
    case "self_reported":
      return TIERS[1].label;
    default:
      return undefined;
  }
}

/** Resolve a tier from either a numeric rank or its string id. */
export function tierMeta(tier: TierRank | TierId): TierMeta {
  const rank = typeof tier === "number" ? tier : ID_TO_RANK[tier];
  return TIERS[rank];
}
